import {
	MouseEvent as ReactMouseEvent,
	TouchEvent as ReactTouchEvent,
	useCallback,
	useEffect,
	useRef,
	useState,
	WheelEvent as ReactWheelEvent,
} from 'react'
import {
	displaySrcSet,
	displayUrl,
	fullSrcSet,
	fullUrl,
	HERO_SIZES,
	preloadImage,
	THUMB_SIZES,
	thumbSrcSet,
	thumbUrl,
} from './image-source'

/**
 * Univerzální galerie fotek.
 *
 * Cíle:
 *  - Rychlé načítání: mini-thumbnaily (~160px) v mřížce, hlavní fotka
 *    v rozumné střední velikosti, plné rozlišení až v zoomu.
 *  - Žádná ruční rotace kdyby to nešlo jinak: EXIF orientace se aplikuje
 *    automaticky (Cloudinary a_auto, proxy auto-rotate, nebo browser default).
 *    Ruční rotace zůstává jako fallback pro případy, kdy EXIF chybí.
 *  - Plynulý zoom a pan myší / kolečkem / na mobilu pinch + drag.
 *  - Preloading sousedních fotek, swipe, klávesy.
 *  - Čistá public API: `<PhotoGallery photos={[...]} />` a hotovo.
 *
 * Komponenta je součástí pevného jádra - konkrétní volba zdroje
 * (Cloudinary vs. /admin/uploads vs. něco dalšího) je v `image-source.ts`,
 * takže různé nasazení (plugin, jiná firma) si to může ohnout tam.
 */

const CLAMP_ROTATION = (deg: number) => ((deg % 360) + 360) % 360
const SWIPE_THRESHOLD_PX = 48
const MIN_ZOOM = 1
const MAX_ZOOM = 6
const ZOOM_STEP = 0.25
const PRELOAD_NEIGHBOURS = 1

const actionBtnLight =
	'inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 hover:border-slate-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-200 disabled:opacity-40 disabled:cursor-not-allowed'
const actionBtnDark =
	'inline-flex items-center gap-1.5 rounded-lg border border-slate-700 bg-slate-900/70 px-3 py-1.5 text-xs font-semibold text-slate-100 transition hover:border-slate-500 hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-300 disabled:opacity-40 disabled:cursor-not-allowed'

const heroArrowBtn =
	'absolute top-1/2 z-10 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full bg-white/85 text-slate-800 shadow-lg ring-1 ring-slate-200 backdrop-blur transition hover:bg-white hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-300'

const clampIndex = (index: number, total: number) => {
	if (!total) return 0
	return ((index % total) + total) % total
}

type Rotations = Record<number, number>

type PhotoGalleryProps = {
	photos: string[]
	keyPrefix?: string
	label?: string
	/** Výška hlavního (ne-fullscreen) náhledu v Tailwind třídách. */
	heightClass?: string
}

export function PhotoGallery({
	photos,
	keyPrefix = 'gallery',
	label,
	heightClass = 'h-72 sm:h-80 md:h-96',
}: PhotoGalleryProps) {
	const total = photos.length
	const [activeIndex, setActiveIndex] = useState(0)
	const [isFullscreen, setIsFullscreen] = useState(false)
	const [rotations, setRotations] = useState<Rotations>({})

	useEffect(() => {
		setActiveIndex(previous => clampIndex(previous, total))
	}, [total])

	// Preload sousedních fotek, ať je navigace svižná.
	useEffect(() => {
		if (!total) return
		for (let step = 1; step <= PRELOAD_NEIGHBOURS; step++) {
			preloadImage(photos[clampIndex(activeIndex + step, total)], 'display')
			preloadImage(photos[clampIndex(activeIndex - step, total)], 'display')
		}
		if (isFullscreen) {
			preloadImage(photos[clampIndex(activeIndex, total)], 'full')
		}
	}, [activeIndex, isFullscreen, photos, total])

	const currentIndex = clampIndex(activeIndex, total)
	const currentPhoto = photos[currentIndex]
	const currentRotation = rotations[currentIndex] ?? 0

	const goTo = useCallback(
		(index: number) => setActiveIndex(clampIndex(index, total || 1)),
		[total],
	)
	const next = useCallback(() => goTo(activeIndex + 1), [activeIndex, goTo])
	const prev = useCallback(() => goTo(activeIndex - 1), [activeIndex, goTo])

	const rotate = useCallback(
		(delta: number) => {
			setRotations(previous => {
				const current = previous[currentIndex] ?? 0
				const nextValue = CLAMP_ROTATION(current + delta)
				const next: Rotations = {}
				for (const [key, value] of Object.entries(previous)) {
					if (Number(key) !== currentIndex) next[Number(key)] = value
				}
				if (nextValue !== 0) next[currentIndex] = nextValue
				return next
			})
		},
		[currentIndex],
	)

	// Klávesnice - pouze když je otevřený fullscreen.
	useEffect(() => {
		if (!isFullscreen) return
		const onKey = (event: KeyboardEvent) => {
			if (event.key === 'Escape') setIsFullscreen(false)
			else if (event.key === 'ArrowRight') next()
			else if (event.key === 'ArrowLeft') prev()
			else if (event.key === 'r' || event.key === 'R') rotate(90)
		}
		window.addEventListener('keydown', onKey)
		return () => window.removeEventListener('keydown', onKey)
	}, [isFullscreen, next, prev, rotate])

	if (!total || !currentPhoto) return null

	const hasMultiple = total > 1

	return (
		<div className="space-y-3">
			{(label || hasMultiple) && (
				<div className="flex flex-wrap items-center justify-between gap-2">
					{label ? (
						<p className="text-sm font-semibold text-slate-700">{label}</p>
					) : (
						<span />
					)}
					{hasMultiple && (
						<span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600 shadow-sm">
							<span className="tabular-nums text-slate-900">{currentIndex + 1}</span>
							<span className="text-slate-400">/</span>
							<span className="tabular-nums">{total}</span>
						</span>
					)}
				</div>
			)}

			<HeroImage
				src={currentPhoto}
				rotation={currentRotation}
				heightClass={heightClass}
				onOpenFullscreen={() => setIsFullscreen(true)}
				onSwipeLeft={hasMultiple ? next : undefined}
				onSwipeRight={hasMultiple ? prev : undefined}
				onPrev={hasMultiple ? prev : undefined}
				onNext={hasMultiple ? next : undefined}
				indexLabel={hasMultiple ? `${currentIndex + 1} / ${total}` : undefined}
			/>

			{hasMultiple ? (
				<div className="-mx-1 overflow-x-auto px-1 pb-1 scroll-smooth">
					<div className="flex gap-2">
						{photos.map((url, index) => (
							<ThumbButton
								key={`${keyPrefix}-thumb-${index}`}
								src={url}
								isActive={index === currentIndex}
								onClick={() => goTo(index)}
								onHoverIntent={() => preloadImage(url, 'display')}
								label={`Zobrazit fotku ${index + 1}`}
							/>
						))}
					</div>
				</div>
			) : null}

			<div className="flex flex-wrap items-center justify-end gap-2">
				<button
					type="button"
					className={actionBtnLight}
					onClick={() => rotate(-90)}
					title="Otočit proti směru hodinových ručiček"
				>
					<span aria-hidden>⟲</span>
					<span>Otočit</span>
				</button>
				<button
					type="button"
					className={actionBtnLight}
					onClick={() => rotate(90)}
					title="Otočit po směru hodinových ručiček"
				>
					<span aria-hidden>⟳</span>
					<span>Otočit</span>
				</button>
				<button
					type="button"
					className={actionBtnLight}
					onClick={() => setIsFullscreen(true)}
					title="Přiblížit / fullscreen"
				>
					<span aria-hidden>🔍</span>
					<span>Přiblížit</span>
				</button>
				<a
					href={currentPhoto}
					target="_blank"
					rel="noreferrer"
					className={actionBtnLight}
					title="Otevřít originál v novém okně"
				>
					Originál
				</a>
			</div>

			{isFullscreen ? (
				<Lightbox
					photos={photos}
					activeIndex={currentIndex}
					rotation={currentRotation}
					onClose={() => setIsFullscreen(false)}
					onPrev={prev}
					onNext={next}
					onRotate={rotate}
				/>
			) : null}
		</div>
	)
}

/* ------------------------------ Hero ------------------------------- */

function HeroImage({
	src,
	rotation,
	heightClass,
	onOpenFullscreen,
	onSwipeLeft,
	onSwipeRight,
	onPrev,
	onNext,
	indexLabel,
}: {
	src: string
	rotation: number
	heightClass: string
	onOpenFullscreen: () => void
	onSwipeLeft?: () => void
	onSwipeRight?: () => void
	onPrev?: () => void
	onNext?: () => void
	indexLabel?: string
}) {
	const touchStartX = useRef<number | null>(null)
	const [isLoaded, setIsLoaded] = useState(false)
	const lowQualitySrc = thumbUrl(src)
	const displaySrc = displayUrl(src)
	const srcSet = displaySrcSet(src)

	// Reset při změně zdroje - nová fotka začíná jako "nenačtená".
	useEffect(() => {
		setIsLoaded(false)
	}, [displaySrc])

	const onTouchStart = (event: ReactTouchEvent<HTMLDivElement>) => {
		const [touch] = Array.from(event.changedTouches)
		touchStartX.current = touch?.clientX ?? null
	}
	const onTouchEnd = (event: ReactTouchEvent<HTMLDivElement>) => {
		if (touchStartX.current === null) return
		const [touch] = Array.from(event.changedTouches)
		if (!touch) return
		const shift = touch.clientX - touchStartX.current
		touchStartX.current = null
		if (Math.abs(shift) < SWIPE_THRESHOLD_PX) return
		if (shift < 0) onSwipeLeft?.()
		else onSwipeRight?.()
	}

	return (
		<div
			className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 shadow-[0_22px_45px_-26px_rgba(15,23,42,0.9)]"
			onTouchStart={onTouchStart}
			onTouchEnd={onTouchEnd}
		>
			<div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(148,163,184,0.22),_transparent_58%)]" />

			{/* LQIP - rozmazaný thumbnail jako placeholder, mizí až je display hotový. */}
			<img
				aria-hidden
				src={lowQualitySrc}
				alt=""
				className={`${heightClass} pointer-events-none absolute inset-0 w-full origin-center object-contain p-3 blur-xl scale-105 transition-opacity duration-300 sm:p-4 ${
					isLoaded ? 'opacity-0' : 'opacity-60'
				}`}
				style={{
					imageOrientation: 'from-image',
					transform: rotation ? `rotate(${rotation}deg) scale(1.05)` : undefined,
				}}
			/>

			<button
				type="button"
				onClick={onOpenFullscreen}
				className="relative block w-full focus-visible:outline-none"
				aria-label="Přiblížit / otevřít fullscreen"
			>
				<img
					src={displaySrc}
					srcSet={srcSet || undefined}
					sizes={srcSet ? HERO_SIZES : undefined}
					alt=""
					loading="eager"
					decoding="async"
					fetchPriority="high"
					onLoad={() => setIsLoaded(true)}
					className={`${heightClass} w-full origin-center object-contain p-3 transition-[opacity,transform] duration-200 sm:p-4 ${
						isLoaded ? 'opacity-100' : 'opacity-0'
					}`}
					style={{
						imageOrientation: 'from-image',
						transform: rotation ? `rotate(${rotation}deg)` : undefined,
					}}
				/>
			</button>

			{!isLoaded && (
				<div className="pointer-events-none absolute inset-0 flex items-center justify-center">
					<div className="h-9 w-9 animate-spin rounded-full border-2 border-white/30 border-t-white/90" />
				</div>
			)}

			{onPrev && (
				<button
					type="button"
					onClick={onPrev}
					className={`${heroArrowBtn} left-3`}
					aria-label="Předchozí fotka"
				>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						width="20"
						height="20"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2.5"
						strokeLinecap="round"
						strokeLinejoin="round"
					>
						<polyline points="15 18 9 12 15 6" />
					</svg>
				</button>
			)}
			{onNext && (
				<button
					type="button"
					onClick={onNext}
					className={`${heroArrowBtn} right-3`}
					aria-label="Další fotka"
				>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						width="20"
						height="20"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2.5"
						strokeLinecap="round"
						strokeLinejoin="round"
					>
						<polyline points="9 18 15 12 9 6" />
					</svg>
				</button>
			)}

			{indexLabel && (
				<div className="pointer-events-none absolute left-3 top-3 rounded-full bg-slate-950/60 px-2.5 py-1 text-xxs font-semibold tracking-wide text-white/90 backdrop-blur">
					{indexLabel}
				</div>
			)}

			<div className="pointer-events-none absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-white/90 px-2.5 py-1 text-xxs font-semibold text-slate-700 opacity-0 shadow-sm backdrop-blur transition group-hover:opacity-100">
				<svg
					xmlns="http://www.w3.org/2000/svg"
					width="12"
					height="12"
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					strokeWidth="2.5"
					strokeLinecap="round"
					strokeLinejoin="round"
					aria-hidden
				>
					<circle cx="11" cy="11" r="8" />
					<line x1="21" y1="21" x2="16.65" y2="16.65" />
					<line x1="11" y1="8" x2="11" y2="14" />
					<line x1="8" y1="11" x2="14" y2="11" />
				</svg>
				Klikni pro přiblížení
			</div>
		</div>
	)
}

/* ---------------------------- Thumb button --------------------------- */

function ThumbButton({
	src,
	isActive,
	onClick,
	onHoverIntent,
	label,
}: {
	src: string
	isActive: boolean
	onClick: () => void
	onHoverIntent?: () => void
	label: string
}) {
	const srcSet = thumbSrcSet(src)
	return (
		<button
			type="button"
			onClick={onClick}
			onMouseEnter={onHoverIntent}
			onFocus={onHoverIntent}
			className={`group relative shrink-0 overflow-hidden rounded-lg transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-300 ${
				isActive
					? 'ring-2 ring-blue-500 ring-offset-2 ring-offset-white shadow-md'
					: 'ring-1 ring-slate-200 hover:ring-slate-300 opacity-80 hover:opacity-100'
			}`}
			aria-label={label}
			aria-current={isActive ? 'true' : undefined}
		>
			<img
				src={thumbUrl(src)}
				srcSet={srcSet || undefined}
				sizes={srcSet ? THUMB_SIZES : undefined}
				alt=""
				loading="lazy"
				decoding="async"
				fetchPriority="low"
				width={80}
				height={56}
				className="h-14 w-20 bg-slate-100 object-cover transition-transform duration-200 group-hover:scale-105"
				style={{ imageOrientation: 'from-image' }}
			/>
			{isActive && (
				<span className="pointer-events-none absolute inset-x-0 bottom-0 h-1 bg-blue-500" />
			)}
		</button>
	)
}

/* ------------------------------ Lightbox ----------------------------- */

type Pan = { x: number; y: number }

function Lightbox({
	photos,
	activeIndex,
	rotation,
	onClose,
	onPrev,
	onNext,
	onRotate,
}: {
	photos: string[]
	activeIndex: number
	rotation: number
	onClose: () => void
	onPrev: () => void
	onNext: () => void
	onRotate: (delta: number) => void
}) {
	const [zoom, setZoom] = useState(1)
	const [pan, setPan] = useState<Pan>({ x: 0, y: 0 })
	const draggingRef = useRef<{
		startX: number
		startY: number
		originX: number
		originY: number
	} | null>(null)
	const pinchRef = useRef<{ distance: number; zoom: number } | null>(null)
	const containerRef = useRef<HTMLDivElement | null>(null)

	const currentPhoto = photos[activeIndex]
	// `useOriginal` přepne lightbox z proxy verze (rychle se načte, max
	// ~2400 px wide) na raw originál (= URL z payloadu, plné rozlišení
	// senzoru). Defaultně admin vidí proxy verzi, kliknutím na „Plný
	// originál" si vyžádá forenzní rozlišení.
	const [useOriginal, setUseOriginal] = useState(false)
	const fullSrcResolved = useOriginal ? currentPhoto : fullUrl(currentPhoto)
	const fullSrcSetValue = useOriginal ? '' : fullSrcSet(currentPhoto)
	const isZoomed = zoom > 1.001

	// Reset zoom + originál flag při změně fotky.
	useEffect(() => {
		setZoom(1)
		setPan({ x: 0, y: 0 })
		setUseOriginal(false)
	}, [activeIndex])

	const clampPan = useCallback((nextPan: Pan, zoomLevel: number): Pan => {
		const container = containerRef.current
		if (!container) return nextPan
		const { width, height } = container.getBoundingClientRect()
		// Max posun = (scale - 1) * velikost / 2, tj. obraz nevyjede úplně ven.
		const maxX = ((zoomLevel - 1) * width) / 2
		const maxY = ((zoomLevel - 1) * height) / 2
		return {
			x: Math.max(-maxX, Math.min(maxX, nextPan.x)),
			y: Math.max(-maxY, Math.min(maxY, nextPan.y)),
		}
	}, [])

	const applyZoom = useCallback(
		(nextZoom: number) => {
			const clamped = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, nextZoom))
			setZoom(clamped)
			setPan(previous => clampPan(previous, clamped))
		},
		[clampPan],
	)

	const onWheel = (event: ReactWheelEvent<HTMLDivElement>) => {
		event.preventDefault()
		const delta = event.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP
		applyZoom(zoom + delta)
	}

	const onDoubleClick = () => {
		applyZoom(isZoomed ? 1 : 2.5)
	}

	const onMouseDown = (event: ReactMouseEvent<HTMLDivElement>) => {
		if (!isZoomed) return
		draggingRef.current = {
			startX: event.clientX,
			startY: event.clientY,
			originX: pan.x,
			originY: pan.y,
		}
	}
	const onMouseMove = (event: ReactMouseEvent<HTMLDivElement>) => {
		const drag = draggingRef.current
		if (!drag) return
		const nextPan = {
			x: drag.originX + (event.clientX - drag.startX),
			y: drag.originY + (event.clientY - drag.startY),
		}
		setPan(clampPan(nextPan, zoom))
	}
	const onMouseUp = () => {
		draggingRef.current = null
	}

	const onTouchStart = (event: ReactTouchEvent<HTMLDivElement>) => {
		if (event.touches.length === 2) {
			const [a, b] = Array.from(event.touches)
			const dx = b.clientX - a.clientX
			const dy = b.clientY - a.clientY
			pinchRef.current = { distance: Math.hypot(dx, dy), zoom }
			draggingRef.current = null
		} else if (event.touches.length === 1) {
			pinchRef.current = null
			const [touch] = Array.from(event.touches)
			draggingRef.current = {
				startX: touch.clientX,
				startY: touch.clientY,
				originX: pan.x,
				originY: pan.y,
			}
		}
	}
	const onTouchMove = (event: ReactTouchEvent<HTMLDivElement>) => {
		if (event.touches.length === 2 && pinchRef.current) {
			event.preventDefault()
			const [a, b] = Array.from(event.touches)
			const dx = b.clientX - a.clientX
			const dy = b.clientY - a.clientY
			const distance = Math.hypot(dx, dy)
			const ratio = distance / pinchRef.current.distance
			applyZoom(pinchRef.current.zoom * ratio)
			return
		}
		if (event.touches.length === 1 && draggingRef.current) {
			const [touch] = Array.from(event.touches)
			if (isZoomed) {
				const nextPan = {
					x: draggingRef.current.originX + (touch.clientX - draggingRef.current.startX),
					y: draggingRef.current.originY + (touch.clientY - draggingRef.current.startY),
				}
				setPan(clampPan(nextPan, zoom))
			}
		}
	}
	const onTouchEnd = (event: ReactTouchEvent<HTMLDivElement>) => {
		// Swipe navigace pouze pokud nejsme přiblížení (jinak bychom rušili pan).
		if (!isZoomed && draggingRef.current && event.touches.length === 0) {
			const drag = draggingRef.current
			const [touch] = Array.from(event.changedTouches)
			if (touch) {
				const shift = touch.clientX - drag.startX
				if (Math.abs(shift) >= SWIPE_THRESHOLD_PX) {
					if (shift < 0) onNext()
					else onPrev()
				}
			}
		}
		draggingRef.current = null
		pinchRef.current = null
	}

	return (
		<div
			className="fixed inset-0 z-50 bg-slate-950/95 p-3 backdrop-blur-sm sm:p-6"
			role="dialog"
			aria-modal="true"
			onClick={onClose}
		>
			<div
				className="mx-auto flex h-full w-full max-w-6xl flex-col"
				onClick={event => event.stopPropagation()}
			>
				<div className="mb-3 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-300">
					<div className="flex items-center gap-2">
						<span>
							Foto {activeIndex + 1} z {photos.length}
						</span>
						{isZoomed ? (
							<span className="rounded-full bg-slate-800/70 px-2 py-0.5">
								{Math.round(zoom * 100)}%
							</span>
						) : null}
					</div>
					<div className="flex flex-wrap items-center gap-2">
						<button
							type="button"
							onClick={() => applyZoom(zoom - ZOOM_STEP)}
							disabled={zoom <= MIN_ZOOM}
							className={actionBtnDark}
							aria-label="Oddálit"
						>
							−
						</button>
						<button
							type="button"
							onClick={() => applyZoom(zoom + ZOOM_STEP)}
							disabled={zoom >= MAX_ZOOM}
							className={actionBtnDark}
							aria-label="Přiblížit"
						>
							+
						</button>
						<button
							type="button"
							onClick={() => applyZoom(1)}
							className={actionBtnDark}
							disabled={!isZoomed}
						>
							100%
						</button>
						<button
							type="button"
							onClick={() => onRotate(-90)}
							className={actionBtnDark}
						>
							⟲
						</button>
						<button
							type="button"
							onClick={() => onRotate(90)}
							className={actionBtnDark}
						>
							⟳
						</button>
						<button
							type="button"
							onClick={() => setUseOriginal(value => !value)}
							className={`${actionBtnDark} ${useOriginal ? '!border-emerald-500 !text-emerald-300' : ''}`}
							title="Načíst plný originál (větší, dlouhé načítání)"
						>
							{useOriginal ? 'Originál ✓' : 'Plný originál'}
						</button>
						<a
							href={currentPhoto}
							target="_blank"
							rel="noreferrer"
							className={actionBtnDark}
							title="Otevřít originál v novém okně / stáhnout"
						>
							Otevřít v novém okně
						</a>
						<button type="button" onClick={onClose} className={actionBtnDark}>
							Zavřít
						</button>
					</div>
				</div>

				<div
					ref={containerRef}
					className={`relative flex min-h-0 flex-1 items-center justify-center overflow-hidden rounded-2xl border border-slate-700 bg-slate-900/70 ${
						isZoomed ? 'cursor-grab active:cursor-grabbing' : 'cursor-zoom-in'
					}`}
					onWheel={onWheel}
					onDoubleClick={onDoubleClick}
					onMouseDown={onMouseDown}
					onMouseMove={onMouseMove}
					onMouseUp={onMouseUp}
					onMouseLeave={onMouseUp}
					onTouchStart={onTouchStart}
					onTouchMove={onTouchMove}
					onTouchEnd={onTouchEnd}
				>
					<div className="pointer-events-none absolute left-3 top-3 z-10 rounded-full bg-black/60 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white">
						{useOriginal ? 'Plný originál' : 'Náhled (rychlé načítání)'}
					</div>
					<img
						src={fullSrcResolved}
						srcSet={fullSrcSetValue || undefined}
						sizes={fullSrcSetValue ? '100vw' : undefined}
						alt=""
						draggable={false}
						className="max-h-full max-w-full select-none object-contain"
						style={{
							imageOrientation: 'from-image',
							transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom}) rotate(${rotation}deg)`,
							transition:
								draggingRef.current || pinchRef.current
									? 'none'
									: 'transform 120ms ease-out',
							willChange: 'transform',
						}}
					/>

					{photos.length > 1 ? (
						<>
							<button
								type="button"
								onClick={onPrev}
								className="absolute left-3 top-1/2 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full bg-slate-900/80 text-white shadow-lg transition hover:bg-slate-800"
								aria-label="Předchozí"
							>
								‹
							</button>
							<button
								type="button"
								onClick={onNext}
								className="absolute right-3 top-1/2 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full bg-slate-900/80 text-white shadow-lg transition hover:bg-slate-800"
								aria-label="Další"
							>
								›
							</button>
						</>
					) : null}

					<div className="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-slate-950/70 px-3 py-1 text-xxs text-slate-300 backdrop-blur">
						dvojklik / kolečko = zoom · držet a tahnout = posun · šipky = další · R =
						otočit
					</div>
				</div>
			</div>
		</div>
	)
}

/**
 * Zpětná kompatibilita - stará komponenta se jmenovala VehiclePhotosSlideshow.
 */
export const VehiclePhotosSlideshow = PhotoGallery
